import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Hero from '../../components/Hero';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faQuestionCircle, faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import '../Support.css';

// Import images
import destekHeroImage from '../../assets/images/destek.jpg';

const SupportEN = () => {
  const faqs = [
    {
      id: 1,
      question: 'What is ProgenX?',
      answer: 'ProgenX is an innovative technology company that offers software development, AI-powered solutions, data science, mobile and web applications, and corporate digital transformation services tailored to businesses\' needs.'
    },
    {
      id: 2,
      question: 'What services does ProgenX offer?',
      answer: 'ProgenX offers education technologies, data science and AI solutions, mobile and web application development, corporate web solutions, corporate software, IoT and smart systems, and smart reporting and analytics services.'
    },
    {
      id: 3,
      question: 'How can I request a project?',
      answer: 'You can submit a project request through our Projects page or Contact page. We will review your requirements and get back to you as soon as possible with a proposal.'
    },
    {
      id: 4,
      question: 'What industries do you serve?',
      answer: 'We serve a wide range of industries including education, healthcare, finance, retail, manufacturing, and more. We develop customized solutions according to each sector\'s specific needs.'
    },
    {
      id: 5,
      question: 'Do you provide ongoing support after project delivery?',
      answer: 'Yes, we provide comprehensive post-delivery support including maintenance, updates, technical support, and continuous improvement services to ensure your solutions stay up to date.'
    }
  ];

  const SupportCard = ({ icon, title, description, buttonText, buttonLink, isExternal }) => {
    const ButtonComponent = isExternal ? 'a' : Link;
    const buttonProps = isExternal 
      ? { href: buttonLink, target: '_blank', rel: 'noopener noreferrer' }
      : { to: buttonLink };
    
    return (
      <div className="support-card">
        <FontAwesomeIcon icon={icon} className="support-card-icon" />
        <h3 className="support-card-title">{title}</h3>
        <p className="support-card-description">{description}</p>
        <ButtonComponent {...buttonProps} className="btn btn-primary">
          {buttonText}
        </ButtonComponent>
      </div>
    );
  };

  return (
    <div className="support">
      <Helmet>
        <title>ProgenX Support | Help Center and Frequently Asked Questions 2026</title>
        <meta name="description" content="ProgenX support page. Frequently asked questions, email and phone support. Get help on ProgenX services, projects, technical issues, and other topics. 24/7 customer support." />
        <meta name="keywords" content="progenx support, progenx help, progenx faq, progenx troubleshooting, progenx customer service, progenx contact, progenx technical support, progenx user guide" />
        <meta property="og:title" content="ProgenX Support | Help Center" />
        <meta property="og:description" content="Comprehensive support center for ProgenX users. Frequently asked questions and contact options." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://progenx.com.tr/en/support" />
        <link rel="canonical" href="https://progenx.com.tr/en/support" />
        <link rel="alternate" hreflang="tr" href="https://progenx.com.tr/destek" />
        <link rel="alternate" hreflang="en" href="https://progenx.com.tr/en/support" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "What is ProgenX?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ProgenX is an innovative technology company that offers software development, AI-powered solutions, data science, mobile and web applications, and corporate digital transformation services tailored to businesses' needs."
                }
              },
              {
                "@type": "Question",
                "name": "What services does ProgenX offer?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "ProgenX offers education technologies, data science and AI solutions, mobile and web application development, corporate web solutions, corporate software, IoT and smart systems, and smart reporting and analytics services."
                }
              },
              {
                "@type": "Question",
                "name": "How can I request a project?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "You can submit a project request through our Projects page or Contact page. We will review your requirements and get back to you as soon as possible with a proposal."
                }
              }
            ]
          })}
        </script>
      </Helmet>
      
      <Hero 
        title="SUPPORT" 
        backgroundImage={destekHeroImage}
      />
      
      <section className="support-content">
        <div className="container">
          <h2 className="section-title">How Can We Help You?</h2>
          <p className="support-intro">
            We are here to provide the best support service to ProgenX users. You can choose the option that suits your needs from the options below.
          </p>
          
          <div className="support-cards">
            <SupportCard 
              icon={faQuestionCircle}
              title="Frequently Asked Questions"
              description="Find solutions quickly with frequently asked questions and answers"
              buttonText="View FAQs"
              buttonLink="#faq-section"
            />
            
            <SupportCard 
              icon={faEnvelope}
              title="Email Support"
              description="Send us an email for your questions, we will respond as soon as possible"
              buttonText="Send Email"
              buttonLink="/en/contact"
            />
            
            <SupportCard 
              icon={faPhone}
              title="Phone Support"
              description="You can reach us on weekdays between 09:00-18:00"
              buttonText="Call Us"
              buttonLink="tel:+905386864516"
              isExternal={true}
            />
          </div>
          
          <section id="faq-section" className="faq-section">
            <h3 className="faq-section-title">Frequently Asked Questions</h3>
            
            <div className="faq-list">
              {faqs.map(faq => (
                <div key={faq.id} className="faq-item">
                  <h4 className="faq-question">
                    <FontAwesomeIcon icon={faQuestionCircle} className="faq-question-icon" />
                    {faq.question}
                  </h4>
                  <p className="faq-answer">{faq.answer}</p>
                </div>
              ))}
            </div>
          </section>
          
          <div className="support-cta">
            <h3 className="support-cta-title">Couldn't Find Your Question?</h3>
            <p className="support-cta-text">You can contact us for more comprehensive support.</p>
            <Link to="/en/contact" className="btn btn-primary">CONTACT US</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SupportEN;
